import type { DatasetUploadJob, DatasetUploadStatus } from "@/api";
import { requiresUploadSourceChange } from "@/upload";
import type { UploadEngineJobStatus, UploadJobView } from "@/upload";
import {
  formatUploadBytes,
  missingChannelNoticeCount,
} from "@/page/datasetUpload/datasetUploadFormatters";

// 업로드 잡 뷰를 사용자 표시 문자열, 진행률, tone 으로 바꾼다. 위젯과 업로드 창이 공유하는 순수 표시 계약이다.

// 더 이상 전송이 진행되지 않는 종단 상태. 진행 카운트에서 제외한다.
export function isTerminal(status: UploadEngineJobStatus): boolean {
  return (
    status === "completed" || status === "failed" || status === "cancelled" || status === "blocked"
  );
}

// 전송이 끝나거나 중단된 창은 닫을 수 있다. 서버 기록은 업로드 현황에서 복구·삭제한다.
export function isDismissable(job: UploadJobView): boolean {
  return isTerminal(job.status);
}

export function showProgress(job: UploadJobView): boolean {
  return (
    job.status === "uploading" ||
    job.status === "completing" ||
    job.status === "paused" ||
    job.status === "awaitingComplete" ||
    (job.status === "processing" && hasStageProgress(job.uploadJob?.status))
  );
}

// publishing 만 서버 진행률(%)을 가지므로 진행 바를 그린다. deriving 은 1차 commit 뒤라 이미
// `completed` 로 바뀐 단계다. 진행도 %가 아니라 step 별 건수이므로 하나의 바로 합칠 수 없다.
function hasStageProgress(serverStatus: DatasetUploadStatus | undefined): boolean {
  return serverStatus === "publishing";
}

// 완료 화면이 알릴 채널 누락 에피소드 수다. 업로드가 이 에피소드를 Training 에서 제외했다. 완료한
// 잡에서만 센다. 문구·톤·버튼이 이 값 하나를 같이 쓴다. 0·모름 판정은 `missingChannelNoticeCount` 다.
export function missingChannelExcluded(job: UploadJobView): number | null {
  return job.status === "completed"
    ? missingChannelNoticeCount(job.uploadJob?.missing_channel_episode_count)
    : null;
}

// 잡 단위 톤. 완료했지만 채널 누락 에피소드를 제외했으면 경고 톤이다. 나머지는 상태의 톤을 따른다.
export function jobTone(job: UploadJobView): string {
  return missingChannelExcluded(job) === null ? statusTone(job.status) : "warning";
}

export function statusTone(status: UploadEngineJobStatus): string {
  if (status === "completed") {
    return "positive";
  }
  if (status === "failed" || status === "blocked") {
    return "danger";
  }
  if (status === "paused" || status === "cancelled") {
    return "muted";
  }
  return "progress";
}

export function formatJobStatus(job: UploadJobView): string {
  switch (job.status) {
    case "queued":
      return "업로드를 기다리고 있습니다.";
    case "initializing":
      return "업로드를 준비하고 있습니다.";
    case "reconciling":
      return "이미 업로드된 파일을 확인하고 있습니다.";
    case "uploading":
      // 엔진이 재연결을 기다린다. 파일 카운터는 바 아래 줄이 그대로 표시한다.
      return job.connection === "waiting"
        ? "네트워크 연결을 기다리고 있습니다. 연결이 돌아오면 자동으로 이어서 올립니다."
        : formatUploading(job);
    case "paused":
      return "업로드를 일시정지했습니다. 다시 시작할 수 있습니다.";
    case "awaitingComplete":
      return "파일 업로드가 끝났습니다. 완료하기 전에 파일을 검토해 주세요.";
    case "completing":
      return "업로드 완료를 처리하고 있습니다.";
    case "processing":
      return formatProcessing(job);
    case "completed":
      return formatCompleted(job);
    case "failed":
      return job.errorMessage ?? "업로드에 실패했습니다. 다시 시도해 주세요.";
    case "cancelled":
      return "업로드가 취소되었습니다.";
    case "blocked":
      return (
        job.errorMessage ??
        "다른 브라우저에서 업로드하고 있습니다. 해당 업로드가 끝난 뒤 다시 시도해 주세요."
      );
  }
}

export function retryButtonText(job: UploadJobView): string | null {
  if (job.status === "failed" && requiresUploadSourceChange(job.uploadJob?.failure)) {
    return null;
  }
  return job.uploadJob?.status === "failed" && job.uploadJob.upload_verified ? "재처리" : "재시도";
}

function formatProcessing(job: UploadJobView): string {
  return formatUploadServerStage(job.uploadJob);
}

// 서버 처리 단계(status)를 한국어 단계 문구로 바꾼다(publishing 이면 진행률 포함). 엔진 뷰 없이
// 서버 잡만 있는 소비처(승격 진행 등)도 그대로 쓴다.
export function formatUploadServerStage(uploadJob: DatasetUploadJob | null | undefined): string {
  switch (uploadJob?.status) {
    case "uploaded":
      return "서버 처리를 기다리고 있습니다.";
    case "fetching":
      return "서버에서 파일을 수집하고 있습니다.";
    case "probing":
      return "Dataset을 확인하고 있습니다.";
    case "validating":
      return "Dataset을 검증하고 있습니다.";
    case "migrating":
      return "Dataset 형식을 변환하고 있습니다.";
    case "publishing":
      return formatStageProgress("데이터를 반영하고 있습니다.", uploadJob.publish_progress_percent);
    default:
      return "서버에서 업로드를 처리하고 있습니다.";
  }
}

// 단계 라벨에 서버 진행률(%)을 덧붙인다. 진행률이 아직 없으면 라벨만 보여준다.
function formatStageProgress(label: string, percent: number | null | undefined): string {
  return percent === null || percent === undefined
    ? label
    : label + " (" + clampPercent(percent) + "%)";
}

/**
 * 완료 문구. finalize 는 카탈로그 확정과 진단 잡 enqueue 를 한 트랜잭션으로 커밋한다. 그래서
 * `done` 에서 "미리보기는 이어서 생성된다" 는 항상 사실이다. 상태로 분기하지 않는다. 진단의 실제
 * 진행은 데이터셋 상세의 processing 축이 소유한다. 업로드 창은 진단을 따로 조회하지 않는다.
 */
function formatCompleted(job: UploadJobView): string {
  const base =
    job.uploadJob?.status === "duplicate_skipped"
      ? "중복 파일을 건너뛰고 완료했습니다."
      : "업로드를 완료했습니다. 미리보기는 이어서 생성됩니다.";
  const missing = missingChannelExcluded(job);
  if (missing === null) {
    return base;
  }
  return `${base} 채널 파일이 빠진 에피소드 ${missing.toLocaleString()}개는 Training에서 제외했습니다.`;
}

export function progressPercent(job: UploadJobView): number {
  if (job.status === "processing" && job.uploadJob?.status === "publishing") {
    return clampPercent(job.uploadJob.publish_progress_percent ?? 0);
  }
  return clampPercent(job.progress.percent);
}

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.min(100, Math.round(value)));
}

// 파일 카운터와 바이트 진행. skipped 를 포함하므로 재개 직후에도 실제 위치에서 시작한다.
function formatUploading(job: UploadJobView): string {
  const doneCount = job.progress.uploadedFiles + job.progress.skippedFiles;
  const loaded = formatUploadBytes(job.progress.loadedBytes);
  const total = formatUploadBytes(job.progress.totalBytes);
  return `파일을 업로드하고 있습니다. ${doneCount.toLocaleString()}/${job.progress.totalFiles.toLocaleString()} · ${loaded} / ${total} (${job.progress.percent}%)`;
}
